import ActiveToggleButton from "@/components/ActiveToggleButton";
import { BaseTable } from "@/components/BaseTable";
import CustomButton from "@/components/common/Button";
import SMSConfigurationService from "@/services/APIs/SMSConfigurationService";
import { tableName } from "@/utils/constant/tableName";
import navigationRoutes from "@/utils/navigationRoutes";
import { SMSConfiguration } from "@/utils/types/smsConfiguration";
import { ProColumns } from "@ant-design/pro-components";
import { useQuery } from "@tanstack/react-query";
import { Popconfirm } from "antd";
import { Edit2, Plus, Trash } from "lucide-react";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import useAdminNavigate from "@/hooks/useAdminNavigate";

const SMSConfigurationListPage = () => {
  const navigate = useAdminNavigate();
  const [searchParams] = useSearchParams();
  const urlPage = parseInt(searchParams.get("page") ?? "1", 10);
  const urlPageSize = parseInt(searchParams.get("pageSize") ?? "10", 10);
  const [page, setPage] = useState(urlPage);
  const [pageSize, setPageSize] = useState(urlPageSize);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["smsConfigurations", page, pageSize],
    queryFn: () =>
      SMSConfigurationService.getAllSMSConfigurations({ page, pageSize }),
  });

  const gridData = data?.results ?? [];
  const total = data?.count ?? 0;

  const handleDelete = async (id: string) => {
    try {
      await SMSConfigurationService.deleteSMSConfiguration(id);
      refetch();
    } catch (error: any) {
      console.error("Failed to delete SMS configuration:", error);
    }
  };

  const columns: ProColumns<SMSConfiguration>[] = [
    {
      title: "SMS Configuration Name",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => a.name!.localeCompare(b.name!),
    },
    {
      title: "Is Active",
      width: 120,
      render: (record: any) => {
        return (
          <ActiveToggleButton
            table={tableName.sms_configuration}
            pk={record.id}
            is_active={record.is_active}
          />
        );
      },
    },
    {
      title: "Actions",
      width: 100,
      valueType: "option",
      render: (_, record) => [
        <CustomButton
          key="edit"
          icon={<Edit2 size={16} />}
          onClick={() =>
            navigate(navigationRoutes.SMS_CONFIGURATION_EDIT(record.id))
          }
          type="text"
          title="Edit SMS Configuration"
        />,
        <Popconfirm
          key="delete"
          title="Are you sure you want to delete this SMS configuration?"
          onConfirm={() => handleDelete(record.id)}
          okText="Yes"
          cancelText="No"
        >
          <CustomButton
            danger
            icon={<Trash size={16} />}
            type="text"
            title="Delete SMS Configuration"
          />
        </Popconfirm>,
      ],
    },
  ];

  return (
    <BaseTable<SMSConfiguration>
      loading={isLoading}
      columns={columns}
      dataSource={gridData}
      page={page}
      pageSize={pageSize}
      total={total}
      rowKey="id"
      toolBarRender={() => [
        <CustomButton
          key="add"
          type="primary"
          icon={<Plus size={16} />}
          onClick={() => navigate(navigationRoutes.SMS_CONFIGURATION_ADD)}
          title="Add New SMS Configuration"
        >
          Add SMS Configuration
        </CustomButton>,
      ]}
      headerTitle="SMS Configuration List"
      onPageChange={(page, size) => {
        setPage(page);
        searchParams.set("page", page.toString());
        searchParams.set("pageSize", size.toString());
        navigate(
          `${navigationRoutes.SMS_CONFIGURATION}?${searchParams.toString()}`
        );
        setPageSize(size ?? 10);
      }}
      request={() => refetch()}
    />
  );
};

export default SMSConfigurationListPage;
